<template>
	<article class="Filters">
		<SelectedFiltersSection>
			<template #header>
				<SelectedFilterSectionHeader />
			</template>
		</SelectedFiltersSection>
		<FilterSelectionAccordion
			v-for="section in visibleFilterSections"
			:key="section.title"
			:title="section.title"
			:filter-types="section.filterTypes"
			:filter-component-loader="section.filterComponentLoader"
			:automatically-open="section.automaticallyOpen"
		/>
	</article>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue';
import { PortalType } from '@studyportals/domain-client';
import { useSearchStore } from '@stores/useSearchStore';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import FilterSelectionAccordion from './FilterSelectionAccordion/FilterSelectionAccordion.vue';
import SelectedFiltersSection from './SelectedFiltersSection/SelectedFiltersSection.vue';
import SelectedFilterSectionHeader from './SelectedFiltersSection/SelectedFilterSectionHeader/SelectedFilterSectionHeader.vue';
import { FilterProcessingService } from '@app/routing/FilterProcessingService';
import { isFilterTypeAllowedForSearchType } from '@domain/mappers/AvailableFilterMappers';
import { SearchType } from '@domain/enums/SearchType';
import { useMobile } from '@composables/useMobile';

type FilterSection = {
	title: string;
	filterTypes: FilterType[];
	automaticallyOpen?: boolean;
	filterComponentLoader: () => Promise<{ default: Component }>;
};

const searchStore = useSearchStore();
const { isMobile } = useMobile();

const isFilterTypeAvailableForPortal = (filterType: FilterType, currentPortalType: PortalType): boolean => {
	if (filterType === FilterType.ORGANISATIONS) {
		// Organisations filter is always visible
		return true;
	}

	const mapper = FilterProcessingService.getInstance().mappers.find((m) => m.filterType === filterType);
	if (!mapper) return true;

	return mapper.getAllExtendedFilterValues(currentPortalType).length > 0;
};

const filterSections: FilterSection[] = [
	{
		title: 'Field of Study',
		filterTypes: [FilterType.DISCIPLINES],
		automaticallyOpen: false,
		filterComponentLoader: () => import('./DisciplineFilter/DisciplineFilter.vue')
	},
	{
		title: 'Education Level',
		filterTypes: [FilterType.EDUCATION_LEVEL],
		filterComponentLoader: () => import('./EducationLevelFilter/EducationLevelFilter.vue'),
	},
	{
		title: 'Location',
		filterTypes: [FilterType.AREA, FilterType.COUNTRY, FilterType.CONTINENT],
		automaticallyOpen: false,
		filterComponentLoader: () => import('./LocationFilter/LocationFilter.vue')
	},
	{
		title: 'University',
		filterTypes: [FilterType.ORGANISATIONS],
		filterComponentLoader: () => import('./OrganisationFilter/OrganisationFilter.vue')
	},
	{
		title: 'Tuition Fee',
		filterTypes: [FilterType.TUITION_FEE],
		filterComponentLoader: () => import('./TuitionFee/TuitionFee.vue')
	},
	{
		title: 'Educational Form',
		filterTypes: [FilterType.EDUCATIONAL_FORM],
		filterComponentLoader: () => import('./EducationalFormFilter/EducationalFormFilter.vue')
	},
	{
		title: 'Duration',
		filterTypes: [FilterType.DURATION],
		filterComponentLoader: () => import('./DurationFilter/DurationFilter.vue')
	},
	{
		title: 'Format',
		filterTypes: [FilterType.DENSITIES],
		filterComponentLoader: () => import('./FormatFilter/FormatFilter.vue')
	},
	{
		title: 'Attendance',
		filterTypes: [FilterType.ATTENDANCE],
		filterComponentLoader: () => import('./AttendanceFilter/AttendanceFilter.vue')
	},
	{
		title: 'Degree Type',
		filterTypes: [FilterType.DEGREE_TYPE],
		filterComponentLoader: () => import('./DegreeFilter/DegreeFilter.vue')
	},
	{
		title: 'Special Programme',
		filterTypes: [FilterType.SPECIAL_PROGRAMMES],
		filterComponentLoader: () => import('./SpecialProgrammeFilter/SpecialProgrammeFilter.vue')
	}
];

const visibleFilterSections = computed<FilterSection[]>(() => {
	const currentPortalType = searchStore.portalType as PortalType;
	const currentSearchType = searchStore.searchType as SearchType;

	return filterSections.filter((section) =>
		section.filterTypes.some((filterType) =>
			isFilterTypeAllowedForSearchType(filterType, currentSearchType)
			&& isFilterTypeAvailableForPortal(filterType, currentPortalType)
		)
	);
});
</script>

<style scoped lang="scss">
	.Filters {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		width: 100%;
		background: $Grey-100;

		@include Breakpoint(Large, ExtraLarge) {
			display: block;
			border-radius: 0.5rem;
			border: 1px solid $Grey-300;
			overflow: hidden;
		}
	}
</style>
